import {
  add,
  mul,
  type Node,
  neg,
  num,
  pow,
  variable,
} from "@/lib/tool-kit/calculus/ast";
import { derivativesUpTo } from "@/lib/tool-kit/calculus/differentiate";
import { compile } from "@/lib/tool-kit/calculus/evaluate";
import { simplify } from "@/lib/tool-kit/calculus/simplify";
import { type PointValue, valueAt } from "@/lib/tool-kit/calculus/value";

/**
 * Taylor polynomials, from symbolic derivatives evaluated at the centre.
 *
 *     Tₙ(x) = Σ f⁽ᵏ⁾(a)/k! · (x − a)ᵏ,    k = 0 … n
 *
 * The coefficients are exact where the algebra allows (1/6, √2/12, cos 1) because each
 * derivative is a symbolic expression evaluated by substitution. The error of Tₙ at a
 * point is bounded by Lagrange's remainder, M·|x − a|ⁿ⁺¹/(n + 1)!, where M bounds
 * |f⁽ⁿ⁺¹⁾| between a and x; M is found by sampling, so the bound is an estimate.
 */

export const MAX_TAYLOR_ORDER = 12;

export interface TaylorTerm {
  k: number;
  /** f⁽ᵏ⁾ as an expression. */
  derivative: Node;
  /** f⁽ᵏ⁾(a). */
  atCentre: PointValue;
  /** Coefficient f⁽ᵏ⁾(a)/k!, exact when f⁽ᵏ⁾(a) has a closed form. */
  coefficient: Node | null;
  coefficientValue: number;
}

export type TaylorResult =
  | {
      kind: "ok";
      terms: TaylorTerm[];
      /** The polynomial in x, terms in increasing degree. */
      polynomial: Node;
      /** f⁽ⁿ⁺¹⁾ when it could be computed (for the remainder bound). */
      nextDerivative: Node | null;
      /** Fewer orders than requested were computed (the expressions grew too large). */
      truncated: boolean;
    }
  | { kind: "undefined"; order: number };

function factorial(k: number): number {
  let value = 1;
  for (let i = 2; i <= k; i += 1) {
    value *= i;
  }
  return value;
}

function polynomialNode(terms: TaylorTerm[], centre: Node, v: string): Node {
  const x = variable(v);
  const shifted = simplify(add(x, neg(centre)));
  const parts: Node[] = [];
  for (const term of terms) {
    if (term.coefficient === null || term.coefficientValue === 0) {
      continue;
    }
    if (term.k === 0) {
      parts.push(term.coefficient);
    } else {
      const base = term.k === 1 ? shifted : pow(shifted, num(term.k));
      parts.push(simplify(mul(term.coefficient, base)));
    }
  }
  if (parts.length === 0) {
    return num(0);
  }
  return parts.length === 1 ? parts[0] : add(...parts);
}

/** The degree-`order` Taylor polynomial of f about the centre (an exact node and its value). */
export function taylorSeries(
  ast: Node,
  v: string,
  centre: Node,
  centreValue: number,
  order: number
): TaylorResult {
  const wanted = Math.min(order, MAX_TAYLOR_ORDER);
  const derivatives = derivativesUpTo(ast, v, wanted + 1);
  const all: Node[] = [ast, ...derivatives];
  const terms: TaylorTerm[] = [];
  for (let k = 0; k <= Math.min(wanted, all.length - 1); k += 1) {
    const atCentre = valueAt(all[k], v, centre, centreValue);
    if (!Number.isFinite(atCentre.value)) {
      return { kind: "undefined", order: k };
    }
    const scale = num(1, factorial(k));
    terms.push({
      k,
      derivative: all[k],
      atCentre,
      coefficient: atCentre.exact ? simplify(mul(atCentre.exact, scale)) : null,
      coefficientValue: atCentre.value / factorial(k),
    });
  }
  const truncated = terms.length < wanted + 1;
  return {
    kind: "ok",
    terms,
    polynomial: polynomialNode(terms, centre, v),
    nextDerivative: all[terms.length] ?? null,
    truncated,
  };
}

/** Evaluate Tₙ at x by Horner's rule on the (x − a) powers. */
export function evaluateTaylor(
  terms: TaylorTerm[],
  centreValue: number,
  x: number
): number {
  const h = x - centreValue;
  let sum = 0;
  for (let i = terms.length - 1; i >= 0; i -= 1) {
    sum = sum * h + terms[i].coefficientValue;
  }
  return sum;
}

export interface RemainderBound {
  /** |f(x) − Tₙ(x)|. */
  actual: number;
  /** Lagrange's bound with M estimated by sampling. */
  bound: number;
  /** The sampled maximum of |f⁽ⁿ⁺¹⁾| between the centre and x. */
  maxDerivative: number;
}

/** Lagrange remainder bound at x, or null if f⁽ⁿ⁺¹⁾ is unavailable or not finite there. */
export function remainderBound(
  ast: Node,
  v: string,
  nextDerivative: Node | null,
  terms: TaylorTerm[],
  centreValue: number,
  x: number
): RemainderBound | null {
  const f = compile(ast, v);
  const actual = Math.abs(f(x) - evaluateTaylor(terms, centreValue, x));
  if (nextDerivative === null || !Number.isFinite(actual)) {
    return null;
  }
  const next = compile(nextDerivative, v);
  const lo = Math.min(centreValue, x);
  const hi = Math.max(centreValue, x);
  let maxDerivative = 0;
  for (let i = 0; i <= 400; i += 1) {
    const sample = Math.abs(next(lo + ((hi - lo) * i) / 400));
    if (!Number.isFinite(sample)) {
      return null;
    }
    maxDerivative = Math.max(maxDerivative, sample);
  }
  const order = terms.length - 1;
  const bound =
    (maxDerivative * Math.abs(x - centreValue) ** (order + 1)) /
    factorial(order + 1);
  return { actual, bound, maxDerivative };
}
